import { useCallback, useEffect } from 'react'
import {
  getControlVisibility,
  getCountryName,
  getProductTypesList,
  getVettingStatusCodeFromStatusValue,
} from '../../../../common'
import {
  ADMIN_CONTROL_TYPE,
  ADMIN_ID_RFI2_ACTION_BUTTONS,
  APPROVE_GROUP,
  CODE_RFI_TYPE_QUESTIONNAIRE,
  COMMENTS,
  FIRST_APPROVED,
  MANUFACTURING_TYPE_ID_PACKAGING,
  RFI_TYPE_LOCATION,
  RFI_TYPE_QUESTIONNAIRE,
  ROUTE_VETTING_SUMMARY_LOCATION,
  STATUS_APPROVE_AND_BYPASS_VFA,
  STATUS_APPROVE_AND_START_VFA,
  STATUS_APPROVE_BUT_HOLD,
  STATUS_INITIATED,
  STATUS_LIST_WITHOUT_COMMENTS,
  STATUS_REJECTED,
  STATUS_RFI3_APPROVED,
} from '../../../../common/commonConstants'
import fireflyData from '../../../../common/fireflyData'
import { buildTimelineEventData } from '../../../../common/helper'
import {
  fastTrackRfi3,
  postRfiAction,
  saveCapabilitiesAndComments,
} from '../../../../services/lcmService'
import { userInfoData } from '../../../Main'
import { useRfi3Context } from './Rfi3Context'

export const useRfi3Actions = (getAllRFI3Data) => {
  const { state, dispatch, deps } = useRfi3Context()
  const { auth, env, sendEvent, navigate, userSettings } = deps
  const {
    rfi3Template,
    rfi3Data,
    userAction,
    userComments,
    refreshRFI3Screen,
    targetTab,
  } = state

  // Open email dialog when userAction set
  useEffect(() => {
    if (state.userAction && !state.productCapabilityDialogSettings?.isVisible) {
      dispatch({ type: 'SET_SHOW_EMAIL_DIALOG', payload: true })
      dispatch({ type: 'SET_RFI_ACTION_IN_PROGRESS', payload: true })
      dispatch({ type: 'SET_RFI_ACTION_FAILED', payload: false })
    }
  }, [
    state.userAction,
    state.productCapabilityDialogSettings?.isVisible,
    dispatch,
  ])

  // Refresh screen when required
  useEffect(() => {
    if (refreshRFI3Screen && typeof getAllRFI3Data === 'function') {
      dispatch({ type: 'SET_REFRESH_SCREEN', payload: false })
      getAllRFI3Data()
    }
  }, [refreshRFI3Screen, dispatch, getAllRFI3Data])

  // Open comments drawer if targetTab=COMMENTS
  useEffect(() => {
    if (targetTab && targetTab.toLowerCase() === COMMENTS.toLowerCase()) {
      dispatch({ type: 'SET_COMMENTS_OPEN', payload: true })
    }
  }, [targetTab, dispatch])

  // Admin control visibility
  useEffect(() => {
    if (userSettings) {
      const enabled = getControlVisibility(
        ADMIN_ID_RFI2_ACTION_BUTTONS,
        ADMIN_CONTROL_TYPE,
        userSettings,
      )
      dispatch({ type: 'SET_RFI3_ACTION_ADMIN_ENABLED', payload: enabled })
    }
  }, [userSettings, dispatch])

  const isRfi3ActionButtonEnabled = useCallback(
    (currentActionButtonType) => {
      let isButtonEnabled = false

      if (rfi3Data && rfi3Data.rfi3_vetting_status_code) {
        const code = rfi3Data.rfi3_vetting_status_code
        const typeUpper = currentActionButtonType.toUpperCase()

        switch (typeUpper) {
          case STATUS_REJECTED.toUpperCase():
            isButtonEnabled = ![4, 6, 7, 8].includes(code)
            break
          case STATUS_APPROVE_AND_START_VFA.toUpperCase():
          case STATUS_APPROVE_AND_BYPASS_VFA.toUpperCase():
          case STATUS_RFI3_APPROVED.toUpperCase():
          case APPROVE_GROUP.toUpperCase():
          case STATUS_APPROVE_BUT_HOLD.toUpperCase():
            isButtonEnabled = ![4, 7, 8].includes(code)
            break
          default:
            console.log('Invalid RFI3 Action button')
        }
      }
      return isButtonEnabled && !!rfi3Data?.all_rfi3_answered
    },
    [rfi3Data],
  )

  const closeEmailDialog = useCallback(
    (isLaunchRfiConfirmation, isSaveAndCloseCase = false) => {
      dispatch({ type: 'SET_SHOW_EMAIL_DIALOG', payload: false })

      if (!isLaunchRfiConfirmation && !isSaveAndCloseCase) {
        dispatch({ type: 'SET_RFI_ACTION_IN_PROGRESS', payload: false })
        dispatch({ type: 'SET_RFI_ACTION_FAILED', payload: false })
        dispatch({ type: 'SET_USER_ACTION', payload: null })
      } else if (isSaveAndCloseCase) {
        dispatch({ type: 'SET_RFI_ACTION_IN_PROGRESS', payload: false })
        dispatch({ type: 'SET_RFI_ACTION_FAILED', payload: false })
        dispatch({ type: 'SET_USER_ACTION', payload: null })
        navigate(ROUTE_VETTING_SUMMARY_LOCATION)
      } else if (isLaunchRfiConfirmation) {
        if (!userAction) {
          dispatch({ type: 'SET_SHOW_CONFIRMATION', payload: false })
          return
        }
        dispatch({ type: 'SET_SHOW_CONFIRMATION', payload: true })
      }
    },
    [dispatch, navigate, userAction],
  )

  const dismissActionConfirmation = useCallback(() => {
    dispatch({ type: 'SET_USER_ACTION', payload: null })
    dispatch({ type: 'SET_SHOW_CONFIRMATION', payload: false })
    dispatch({ type: 'SET_USER_COMMENTS', payload: '' })
  }, [dispatch])

  const closeRFIActionConfirmationModal = useCallback(() => {
    dispatch({ type: 'SET_RFI_ACTION_IN_PROGRESS', payload: false })
    dispatch({ type: 'SET_RFI_ACTION_FAILED', payload: false })
    dismissActionConfirmation()
  }, [dispatch, dismissActionConfirmation])

  const pushDataToFirefly = useCallback(
    (action, prevAction) => {
      if (!rfi3Data) return
      const rfi = 'RFI3'
      const tag = `${rfi} ${action} action for location`
      const keyBase = `selected_${rfi.toLowerCase()}_${prevAction}_to_${action.replace(
        / /g,
        '_',
      )}`

      const updated = rfi3Data?.last_vetting_status?.status_updated_on
      const dt = updated ? new Date(updated) : null
      const daysSince =
        dt && !isNaN(dt.getTime())
          ? Math.floor((Date.now() - dt.getTime()) / (1000 * 60 * 60 * 24))
          : undefined

      if (daysSince === undefined) return

      const list = getProductTypesList(RFI_TYPE_LOCATION, rfi3Data) || []
      list.forEach((productCapability) => {
        const eventData = {
          user: userInfoData,
          view: {
            globalName: fireflyData.view.globalName,
            name: tag,
            type: fireflyData.view.type.button,
          },
          event: {
            type: fireflyData.event.type.click,
          },
          customInteraction: {
            location: `${rfi} - ${prevAction} to ${action}`,
            key: rfi3Data?.status_version
              ? `${keyBase}_${rfi3Data?.status_version}`
              : `${keyBase}_1`,
            value: `${rfi3Data.supplier_id}-${rfi3Data.location_id}`,
          },
          customMetrics: {
            metric1: getCountryName(rfi3Data, rfi),
            metric2: productCapability,
            metric3: rfi3Data?.manufacturing_type,
            metric4: daysSince,
          },
        }

        const payload = {
          event: {
            type: 'CLICK',
            id: eventData.customInteraction.key,
            value: String(daysSince),
          },
          supplier_info: {
            id: rfi3Data?.supplier_id,
            location_id: rfi3Data?.location_id,
            manufacturing_type: rfi3Data?.manufacturing_type,
            action: productCapability,
          },
          client: {
            page_name: eventData.customInteraction.location,
            version: eventData.customMetrics.metric1,
            employee_login_id: userInfoData?.id,
          },
        }

        sendEvent(payload)
      })
    },
    [rfi3Data, sendEvent],
  )

  const publishRFITimelineData = useCallback(
    (newVettingStatusCode) => {
      if (
        ['6', '7', '8', '10'].includes(String(newVettingStatusCode)) &&
        rfi3Data?.rfi3_vetting_status_code === 1
      ) {
        const eventData = buildTimelineEventData(
          FIRST_APPROVED,
          rfi3Data,
          userInfoData,
          CODE_RFI_TYPE_QUESTIONNAIRE.toUpperCase(),
        )
        sendEvent(eventData)
      }
    },
    [rfi3Data, sendEvent],
  )

  const onActionConfirmation = useCallback(async () => {
    dispatch({ type: 'SET_RFI_ACTION_IN_PROGRESS', payload: true })
    dispatch({ type: 'SET_RFI_ACTION_FAILED', payload: false })

    const vettingStatusCodeToSend = await getVettingStatusCodeFromStatusValue(
      userAction?.toUpperCase(),
    )

    dispatch({ type: 'SET_SHOW_CONFIRMATION', payload: false })

    try {
      const actionPayload = {
        manufacturing_type: rfi3Data.manufacturing_type, // NOTE: for BE service, TODO remove when decom
        manufacturing_type_id: rfi3Data.manufacturing_type_id,
        vetting_type: RFI_TYPE_QUESTIONNAIRE,
        supplier_id: rfi3Data.supplier_id,
        supplier_name: rfi3Data.supplier_name,
        location_id: rfi3Data.location_id,
        location_name:
          rfi3Data.fixed_sections?.factory_contact_information?.location_name,
        vetting_status_code: vettingStatusCodeToSend,
        review_status: userAction,
        source: rfi3Data.source,
        vetting_status_comments: !STATUS_LIST_WITHOUT_COMMENTS.includes(
          userAction.toUpperCase() ||
            rfi3Data.manufacturing_type_id === MANUFACTURING_TYPE_ID_PACKAGING,
        )
          ? userComments
          : '',
        vetted_by_id: auth.session.userInfo.lanId,
        vetted_by_email: auth.session.userInfo.email,
        vetted_by_name: auth.session.userInfo.fullName,
        capability_vetting_status:
          userAction === STATUS_RFI3_APPROVED ||
          userAction.toLowerCase() === STATUS_APPROVE_BUT_HOLD
            ? rfi3Data?.capability_vetting_status
            : null,
      }

      await postRfiAction(actionPayload)

      publishRFITimelineData(vettingStatusCodeToSend)
      dispatch({ type: 'SET_USER_ACTION', payload: null })
      dispatch({ type: 'SET_USER_COMMENTS', payload: '' })
      dispatch({ type: 'SET_RFI_ACTION_IN_PROGRESS', payload: false })
      dispatch({ type: 'SET_RFI_ACTION_FAILED', payload: false })
      pushDataToFirefly(userAction, STATUS_INITIATED)
      dispatch({ type: 'SET_REFRESH_SCREEN', payload: true })
    } catch (error) {
      dispatch({ type: 'SET_RFI_ACTION_IN_PROGRESS', payload: false })
      dispatch({ type: 'SET_RFI_ACTION_FAILED', payload: true })
      dispatch({ type: 'SET_REFRESH_SCREEN', payload: false })
    }
  }, [
    rfi3Data,
    userAction,
    userComments,
    auth,
    dispatch,
    publishRFITimelineData,
    pushDataToFirefly,
  ])

  const confirmBypassReaction = useCallback(
    async (isBypassRfi3, bypassComments) => {
      dispatch({ type: 'SET_SHOW_BYPASS', payload: false })
      try {
        if (isBypassRfi3 && rfi3Data) {
          const fastTrackPayload = {
            supplier_id: rfi3Data?.supplier_id,
            location_id: rfi3Data?.location_id,
            manufacturing_type: rfi3Data?.manufacturing_type,
            manufacturing_type_id: rfi3Data?.manufacturing_type_id,
            fast_track_reason: bypassComments,
            read_only: true,
          }
          await fastTrackRfi3(fastTrackPayload)
          dispatch({ type: 'SET_REFRESH_SCREEN', payload: true })
        }
        dispatch({ type: 'SET_FAST_TRACK_IN_PROGRESS', payload: false })
        dispatch({ type: 'SET_FAST_TRACK_FAILED', payload: false })
      } catch (error) {
        dispatch({ type: 'SET_FAST_TRACK_IN_PROGRESS', payload: false })
        dispatch({ type: 'SET_FAST_TRACK_FAILED', payload: true })
      }
    },
    [dispatch, rfi3Data],
  )

  const closeRFIProductCapabilitiesConfirmationModal = useCallback(() => {
    dispatch({
      type: 'SET_PRODUCT_CAPABILITY_DIALOG_SETTINGS',
      payload: { isVisible: false, isReadOnly: false },
    })
    dispatch({ type: 'SET_USER_ACTION', payload: null })
  }, [dispatch])

  const onCapabilityNextActionConfirmation = useCallback(
    async (newComments, newCapabilities, isCapabilityManagementMode) => {
      if (!rfi3Data) return

      const isApprovalFlow = !!userAction

      dispatch({ type: 'SET_RFI_NEXT_ACTION_PC_IN_PROGRESS', payload: true })
      dispatch({ type: 'SET_RFI_NEXT_ACTION_PC_FAILED', payload: false })

      const actionPayload = {
        vetting_type: RFI_TYPE_QUESTIONNAIRE,
        supplier_id: rfi3Data.supplier_id,
        supplier_name:
          rfi3Data?.fixed_sections?.supplier_contact_information?.supplier_name,
        manufacturing_type_id: rfi3Data?.manufacturing_type_id,
        manufacturing_type: rfi3Data?.manufacturing_type,
        vetting_status_comments: newComments,
        capability_vetting_status: newCapabilities,
        location_id: rfi3Data?.location_id,
        location_name: rfi3Data?.location_name,
      }

      try {
        const rfiNextActionResponse =
          await saveCapabilitiesAndComments(actionPayload)
        if (rfiNextActionResponse) {
          dispatch({
            type: 'SET_RFI_NEXT_ACTION_PC_IN_PROGRESS',
            payload: false,
          })
          dispatch({
            type: 'SET_RFI_NEXT_ACTION_PC_FAILED',
            payload: false,
          })
          // dispatch({ type: 'SET_SHOW_EMAIL_DIALOG', payload: true })
          dispatch({
            type: 'SET_PRODUCT_CAPABILITY_DIALOG_SETTINGS',
            payload: { isVisible: false, isReadOnly: false },
          })
          dispatch({ type: 'SET_USER_COMMENTS', payload: newComments })
          dispatch({
            type: 'SET_DATA',
            payload: {
              ...rfi3Data,
              capability_vetting_status: newCapabilities,
            },
          })
          if (isApprovalFlow) {
            dispatch({ type: 'SET_SHOW_EMAIL_DIALOG', payload: true })
          } else {
            dispatch({ type: 'SET_SHOW_EMAIL_DIALOG', payload: false })
            dispatch({ type: 'SET_SHOW_CONFIRMATION', payload: false })
            dispatch({ type: 'SET_RFI_ACTION_IN_PROGRESS', payload: false })
            dispatch({ type: 'SET_RFI_ACTION_FAILED', payload: false })
          }
          if (isCapabilityManagementMode) {
            dispatch({ type: 'SET_REFRESH_SCREEN', payload: true })
          }
        }
      } catch (error) {
        dispatch({
          type: 'SET_RFI_NEXT_ACTION_PC_IN_PROGRESS',
          payload: false,
        })
        dispatch({
          type: 'SET_RFI_NEXT_ACTION_PC_FAILED',
          payload: true,
        })
        dispatch({ type: 'SET_SHOW_EMAIL_DIALOG', payload: false })
      }
    },
    [dispatch, rfi3Data, userAction],
  )

  const onRfi3ApproveClick = useCallback(
    (status) => {
      if (
        rfi3Template?.show_approval_popup &&
        status === STATUS_RFI3_APPROVED
      ) {
        dispatch({
          type: 'SET_PRODUCT_CAPABILITY_DIALOG_SETTINGS',
          payload: { isVisible: true, isReadOnly: false },
        })
      }
      dispatch({ type: 'SET_USER_ACTION', payload: status })
    },
    [dispatch, rfi3Template],
  )

  const handleFastTrackClick = useCallback(() => {
    dispatch({ type: 'SET_SHOW_BYPASS', payload: true })
    dispatch({ type: 'SET_FAST_TRACK_IN_PROGRESS', payload: true })
    dispatch({ type: 'SET_FAST_TRACK_FAILED', payload: false })
  }, [dispatch])

  const handleRejectClick = useCallback(() => {
    dispatch({ type: 'SET_USER_ACTION', payload: STATUS_REJECTED })
  }, [dispatch])

  const handleApproveClick = useCallback(
    (status) => {
      onRfi3ApproveClick(status)
    },
    [onRfi3ApproveClick],
  )

  const setUserComments = useCallback(
    (value) => {
      dispatch({ type: 'SET_USER_COMMENTS', payload: value })
    },
    [dispatch],
  )

  const setIsCommentsOpen = useCallback(
    (flag) => dispatch({ type: 'SET_COMMENTS_OPEN', payload: flag }),
    [dispatch],
  )

  const setIsAuditTrailOpen = useCallback(
    (flag) => dispatch({ type: 'SET_AUDIT_OPEN', payload: flag }),
    [dispatch],
  )

  const setIsShowBypass = useCallback(
    (flag) => dispatch({ type: 'SET_SHOW_BYPASS', payload: flag }),
    [dispatch],
  )

  const onLastRfi3ActivityChange = useCallback(
    (lastRFI3Activity) => {
      dispatch({
        type: 'SET_FAST_TRACK_AVAILABLE',
        payload:
          lastRFI3Activity?.toLowerCase() === STATUS_INITIATED.toLowerCase(),
      })
    },
    [dispatch],
  )

  const handleCloseBypassModal = useCallback(() => {
    dispatch({ type: 'SET_SHOW_BYPASS', payload: false })
    dispatch({ type: 'SET_FAST_TRACK_IN_PROGRESS', payload: false })
    dispatch({ type: 'SET_FAST_TRACK_FAILED', payload: false })
  }, [dispatch])

  const setProductCapabilityDialogSettings = useCallback(
    (settings) => {
      dispatch({
        type: 'SET_PRODUCT_CAPABILITY_DIALOG_SETTINGS',
        payload: settings,
      })
    },
    [dispatch],
  )

  const handleViewCapabilityAuditHistory = useCallback(() => {
    dispatch({
      type: 'SET_CAPABILITY_AUDIT_SETTINGS',
      payload: { isVisible: true, isReadOnly: true },
    })
  }, [dispatch])

  const handleOnCapabilityAuditDrawerClose = useCallback(() => {
    dispatch({
      type: 'SET_CAPABILITY_AUDIT_SETTINGS',
      payload: { isVisible: false, isReadOnly: true },
    })
  }, [dispatch])

  return {
    // state bits
    isFastTrackInProgress: state.isFastTrackInProgress,
    isFastTrackFailed: state.isFastTrackFailed,
    isFastTrackAvailable: state.isFastTrackAvailable,
    isShowBypass: state.isShowBypass,
    isShowEmailDialog: state.isShowEmailDialog,
    isShowConfirmation: state.isShowConfirmation,
    isRfiActionInProgress: state.isRfiActionInProgress,
    isRfiActionFailed: state.isRfiActionFailed,
    refreshRFI3Screen: state.refreshRFI3Screen,
    isRfi3ActionAdminEnabled: state.isRfi3ActionAdminEnabled,
    productCapabilityDialogSettings: state.productCapabilityDialogSettings,
    isRfiNextActionPCInProgress: state.isRfiNextActionPCInProgress,
    isRfiNextActionPCFailed: state.isRfiNextActionPCFailed,
    isCommentsOpen: state.isCommentsOpen,
    isAuditTrailOpen: state.isAuditTrailOpen,
    userAction: state.userAction,
    userComments: state.userComments,
    capabilityAudit: state.capabilityAudit,

    // setters
    setUserComments,
    setIsCommentsOpen,
    setIsAuditTrailOpen,
    setIsShowBypass,

    // logic
    isRfi3ActionButtonEnabled,
    closeEmailDialog,
    closeRFIActionConfirmationModal,
    onActionConfirmation,
    confirmBypassReaction,
    onCapabilityNextActionConfirmation,
    closeRFIProductCapabilitiesConfirmationModal,
    onRfi3ApproveClick,
    handleFastTrackClick,
    handleRejectClick,
    handleApproveClick,
    onLastRfi3ActivityChange,
    handleCloseBypassModal,
    setProductCapabilityDialogSettings,
    handleViewCapabilityAuditHistory,
    handleOnCapabilityAuditDrawerClose,
  }
}
